import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import auth from "../../fire";
import { useAuthState } from "react-firebase-hooks/auth";
import axiosClient from "../../axiosClient";
import createTemplateExerciseDetailsText from "../../functions/createTemplateExerciseDetailsText";
import PageHeader from "../../components/PageHeader/PageHeader";
import ExerciseNameEditor from "../../components/ExerciseNameEditor/ExerciseNameEditor";
import MessageModal from "../../components/MessageModal/MessageModal";
import SetList from "../../components/SetList/SetList";
import { use100vh } from "react-div-100vh";
import NotesInput from "../../components/NotesInput/NotesInput";
import StickyStopwatch from "../../components/StickyStopwatch/StickyStopwatch";
import PreviousExerciseSessions from "../../components/PreviousExerciseSessions/PreviousExerciseSessions";
import TextSkeleton from "../../components/SkeletonComponents/TextSkeleton";
import FullWidthAsyncButton from "../../components/FullWidthButton/FullWidthAsyncButton";

const Exercise: React.FC = () => {
  const { workoutId, exerciseId } = useParams() as {
    workoutId: string;
    exerciseId: string;
  };
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [currentExercise, setCurrentExercise] = useState<Exercise | null>(null);
  const [parentWorkout, setParentWorkout] = useState<Workout | null>(null);
  const [exerciseNameSuggestions, setExerciseNameSuggestions] = useState<
    String[]
  >([]);

  const [templateExerciseDetailsText, setTemplateExerciseDetailsText] =
    useState<string>("");

  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });

  const windowHeight = use100vh();

  const [stopwatchHeight, setStopwatchHeight] = useState(0);
  const [previousExerciseSessionHeight, setPreviousExerciseSessionHeight] =
    useState(88);

  // StickyStopwatch component is rendered once and persists
  const stickyStopwatch = (
    <StickyStopwatch
      position={previousExerciseSessionHeight + 63}
      onHeightChange={(height) => {
        setStopwatchHeight(height);
      }}
    />
  );

  // PreviousExerciseSessions component is rendered once and persists
  const previousExerciseSessionsComponent = (
    <PreviousExerciseSessions
      currentExercise={currentExercise}
      onHeightChange={(height) => {
        setPreviousExerciseSessionHeight(height);
      }}
    />
  );

  const fetchExerciseData = useCallback(async () => {
    try {
      const fetchExerciseResponse = await axiosClient.get(
        `/api/exercises/${exerciseId}`,
        {
          params: {
            getParentWorkout: true,
          },
        }
      );
      const exerciseAndParentWorkoutData = await fetchExerciseResponse.data;
      setCurrentExercise(exerciseAndParentWorkoutData.exerciseData);
      setParentWorkout(exerciseAndParentWorkoutData.parentWorkoutData);
      return exerciseAndParentWorkoutData.exerciseData;
    } catch (e) {
      console.error("Error fetching initial exercise data: " + e);
    }
  }, [exerciseId]);

  const fetchExerciseNameSuggestions = useCallback(async () => {
    try {
      const fetchExerciseResponse = await axiosClient.get(
        `/api/exercises/allExercisesDoneByUser`
      );

      setExerciseNameSuggestions(fetchExerciseResponse.data);
    } catch (error) {
      console.error("Error fetching previous exercise names:", error);
    }
  }, []);

  const fetchParentWorkoutTemplate = async (workoutTemplateId: string) => {
    try {
      const fetchWorkoutTemplateResponse = await axiosClient.get(
        `/api/workout-templates/${workoutTemplateId}`
      );
      const workoutTemplateData = await fetchWorkoutTemplateResponse.data;
      return workoutTemplateData;
    } catch (e) {
      console.error("Error fetching exercises parent workout template: " + e);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
    }

    const fetchAllInitialPageData = async () => {
      const [fetchExerciseDataResponse] = await Promise.all([
        fetchExerciseData(),
        fetchExerciseNameSuggestions(),
      ]);

      const initialExerciseData = await fetchExerciseDataResponse;

      if (initialExerciseData && initialExerciseData.exerciseTemplate) {
        const parentWorkoutTemplate = await fetchParentWorkoutTemplate(
          initialExerciseData.workoutTemplate
        );
        parentWorkoutTemplate.exercises.forEach(
          (fetchedExerciseTemplate: ExerciseTemplate) => {
            if (
              fetchedExerciseTemplate._id ===
              initialExerciseData.exerciseTemplate
            ) {
              const templateDetailsText = createTemplateExerciseDetailsText(
                fetchedExerciseTemplate
              );
              setTemplateExerciseDetailsText(templateDetailsText);
            }
          }
        );
      }

      setIsInitialLoading(false);
    };

    fetchAllInitialPageData();
  }, [
    fetchExerciseData,
    fetchExerciseNameSuggestions,
    loading,
    navigate,
    user,
  ]);

  const optimisticExerciseUpdate = async <K extends keyof Exercise>(
    field: K,
    value: Exercise[K]
  ) => {
    if (!currentExercise) return;

    const exerciseStateBeforeUpdateBackup = currentExercise;
    const updatedExerciseState = { ...currentExercise, [field]: value };

    // Optimistically update state
    setCurrentExercise(updatedExerciseState);

    try {
      const updateExerciseResponse = await axiosClient.put(
        `/api/exercises/${exerciseId}`,
        updatedExerciseState
      );

      if (updateExerciseResponse.status !== 200) {
        throw new Error(updateExerciseResponse.data.message);
      }
    } catch (error) {
      console.error(`Failed to update exercise ${field}:`, error);
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Updating Exercise",
        message: `Failed to update exercise ${field}. Some changes was not saved. Please try again or contact support.`,
      });
      setCurrentExercise(exerciseStateBeforeUpdateBackup);
    }
  };

  const handleExerciseNameChange = (newName: string) => {
    optimisticExerciseUpdate("name", newName);
  };

  const handleSetsUpdated = (updatedSets: ExerciseSet[]) => {
    optimisticExerciseUpdate("sets", updatedSets);
  };

  const handleNotesUpdated = (newNotes: string) => {
    optimisticExerciseUpdate("notes", newNotes);
  };

  return (
    <>
      {stickyStopwatch}
      {previousExerciseSessionsComponent}
      {isInitialLoading || !currentExercise ? (
        <ExercisePageLoadingState parentWorkoutId={workoutId} />
      ) : (
        <>
          <PageHeader
            backLink={`/workout/${workoutId}`}
            backLinkState={{
              workoutTitle: parentWorkout ? parentWorkout.title : null,
              workoutDate: parentWorkout ? parentWorkout.date : null,
            }}
            pageTitle="Exercise Details"
          />
          <div
            className="overflow-y-scroll"
            style={{
              height: `calc(${windowHeight}px - 63px - ${previousExerciseSessionHeight}px - ${stopwatchHeight}px - 63px)`,
            }}
          >
            <div className="pt-4 px-2">
              <ExerciseNameEditor
                placeholder="Exercise name"
                exerciseName={currentExercise.name}
                onExerciseNameChange={handleExerciseNameChange}
                exerciseNameSuggestions={exerciseNameSuggestions}
              />
            </div>

            <SetList
              sets={currentExercise.sets}
              onSetsUpdated={handleSetsUpdated}
            />

            {templateExerciseDetailsText && (
              <div className="px-2 pb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Set Programming Details
                </p>
                <p className="text-sm text-indigo-600">
                  {templateExerciseDetailsText}
                </p>
              </div>
            )}

            <NotesInput
              placeholder="Put any notes for this exercise here! Examples: set difficulty, weight increases, intensity techniques, form cues, etc."
              notes={currentExercise.notes}
              onNotesUpdated={handleNotesUpdated}
            />

            <MessageModal
              isOpen={modalState.isOpen}
              onModalClose={() => {
                setModalState({ ...modalState, isOpen: false });
              }}
              type={modalState.type}
              title={modalState.title}
              message={modalState.message}
            />
          </div>
        </>
      )}
    </>
  );
};

export default Exercise;

interface ExercisePageLoadingStateProps {
  parentWorkoutId: string;
}

const ExercisePageLoadingState: React.FC<ExercisePageLoadingStateProps> = ({
  parentWorkoutId,
}) => {
  return (
    <>
      <PageHeader
        backLink={`/workout/${parentWorkoutId}`}
        pageTitle="Exercise Details"
      />
      <div className="pt-4 px-2">
        <ExerciseNameEditor placeholder="Exercise name" isLoading={true} />
      </div>

      <h2 className="pt-4 pb-1 px-2 block text-sm font-medium text-gray-700">
        Sets
      </h2>

      <div className="px-2 py-1">
        <TextSkeleton height="172px" className="w-full" />
      </div>

      <div className="py-4 px-2">
        <FullWidthAsyncButton
          isSkeleton={true}
          text="Add Set"
          loadingText=""
          onClick={async () => {}}
        />
      </div>

      <NotesInput
        placeholder="Put any notes for this exercise here! Examples: set difficulty, weight increases, intensity techniques, form cues, etc."
        isLoading={true}
      />
    </>
  );
};
